/**
 * Roles and the permission matrix. The Worker checks every protected route
 * against this; the frontend uses the same table only to decide what to show.
 * Hiding a button is never the access control. PLAN.md 3.5, 8.
 *
 * Access to a specific project or template is a separate, DB-backed check
 * (user_code + template_code membership). This matrix answers "may this role
 * ever do X", not "may this user do X to that record".
 */

export const ROLES = ['admin', 'manager', 'executive'] as const;
export type Role = (typeof ROLES)[number];

export const EXEC_SUBTYPES = ['view', 'editor'] as const;
export type ExecSubtype = (typeof EXEC_SUBTYPES)[number];

/** Role and subtype collapsed into the four identities the matrix is written in. */
export type Actor = 'admin' | 'manager' | 'executive_view' | 'executive_editor';

export function actorOf(role: Role, subtype: ExecSubtype | null): Actor {
  if (role === 'executive') {
    if (subtype === 'editor') return 'executive_editor';
    if (subtype === 'view') return 'executive_view';
    // An executive with no subtype is a data error; fail closed to the narrowest role.
    return 'executive_view';
  }
  return role;
}

export const CAPABILITIES = [
  'account.manage',          // add/remove managers and executives, set subtypes
  'label.manage',
  'template.author',         // upload .docx, edit, mark placeholders, publish
  'template.customize',      // fork a client-specific copy (separate R2 object)
  'template.readFull',       // receive the full AST (prose)
  'template.readSkeleton',   // receive the skeleton only
  'project.create',
  'project.read',            // scoped per role by the DB query
  'shareLink.manage',
  'document.fill',
  'document.chooseClauses',
  'document.editProse',      // edit the wording of their own document instance
  'document.addClauses',
  'document.download',
  'audit.read',
  'review.ask',              // ask the admin one question about a template, from his own draft (owner 10-21)
  'review.answer',           // read and answer review requests (admins only)
] as const;
export type Capability = (typeof CAPABILITIES)[number];

const MATRIX: Record<Actor, ReadonlySet<Capability>> = {
  admin: new Set<Capability>([
    'account.manage', 'label.manage', 'template.author', 'template.customize',
    'template.readFull', 'project.create', 'project.read', 'shareLink.manage', 'audit.read', 'review.answer',
  ]),
  manager: new Set<Capability>([
    'template.customize', 'template.readFull', 'project.create', 'project.read', 'shareLink.manage',
  ]),
  executive_view: new Set<Capability>([
    'template.readSkeleton', 'project.read', 'document.fill', 'document.chooseClauses', 'document.download', 'review.ask',
  ]),
  executive_editor: new Set<Capability>([
    'template.readFull', 'project.read', 'document.fill', 'document.chooseClauses',
    'document.editProse', 'document.addClauses', 'document.download', 'review.ask',
  ]),
};

export function can(actor: Actor, capability: Capability): boolean {
  return MATRIX[actor].has(capability);
}

/** Which template payload mode a role receives. PLAN.md 3.4. */
export function templateModeFor(actor: Actor): 'full' | 'skeleton' {
  return can(actor, 'template.readFull') ? 'full' : 'skeleton';
}
